import { z } from 'zod'

export const INSURANCE_INFORMATION_MAX_ENTRIES = 100
export const INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS = 500

const behaviorSchema = z.object({
  voiceProfile: z
    .object({
      speakerKey: z.enum(['jessica', 'bella', 'sarah', 'eric']),
      speakingPace: z.enum(['RELAXED', 'BALANCED', 'BRISK']).optional(),
      backgroundAmbience: z.enum(['OFFICE', 'QUIET']).optional(),
    })
    .strict()
    .optional(),
  // Canonical intake preferences; omission preserves the server's ON defaults.
  draftIntakePreferences: z
    .object({
      proactiveAgeQuestioning: z.boolean(),
      proactiveInsuranceQuestioning: z.boolean(),
    })
    .strict()
    .optional(),
  aiName: z.string().min(1).max(40),
  firstMessage: z.string().min(1).max(500),
  language: z.enum(['English', 'Spanish', 'Russian']),
  approvedInstructions: z.array(z.string().min(1).max(500)).max(20),
  insuranceInformation: z
    .array(z.string().min(1).max(INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS))
    .max(INSURANCE_INFORMATION_MAX_ENTRIES),
  acceptedInsurances: z
    .object({
      schemaVersion: z.literal(1),
      carriers: z
        .array(
          z
            .object({
              name: z.string().trim().min(1).max(120),
              aliases: z.array(z.string().trim().min(1).max(80)).max(12),
            })
            .strict()
        )
        .max(100),
    })
    .strict()
    .nullable()
    .optional(),
  escalationMode: z.enum(['NONE', 'FRONT_DESK', 'VOICEMAIL']),
  keypadLanguageSwitchEnabled: z.boolean(),
  maxDurationSeconds: z.number().int().min(60).max(1800),
  backgroundSound: z.enum(['off', 'office']),
  endCallMessage: z.string().min(1).max(500),
})

export const assistantBehaviorResponseSchema = z.discriminatedUnion(
  'configured',
  [
    z.object({
      configured: z.literal(false),
      schemaVersion: z.literal(1),
      reconciliationStatus: z.literal('NOT_APPLICABLE').optional(),
      initialBehavior: behaviorSchema.optional(),
    }),
    z.object({
      configured: z.literal(true),
      schemaVersion: z.literal(1),
      revision: z.number().int().positive(),
      behaviorFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
      reconciliationStatus: z
        .enum(['IN_SYNC', 'STALE', 'NOT_MATERIALIZED'])
        .optional(),
      behavior: behaviorSchema,
    }),
  ]
)

export const assistantBehaviorUpdateSchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  behavior: behaviorSchema,
})

export const voiceGreetingUpdateSchema = z.object({
  expectedRevision: z.number().int().positive(),
  firstMessage: z.string().trim().min(1).max(500),
})

export const textGreetingResponseSchema = z.discriminatedUnion('configured', [
  z
    .object({
      configured: z.literal(false),
      schemaVersion: z.literal(1),
      revision: z.literal(0),
      template: z.string().min(1).max(500),
    })
    .strict(),
  z
    .object({
      configured: z.literal(true),
      schemaVersion: z.literal(1),
      revision: z.number().int().positive(),
      template: z.string().min(1).max(500),
      templateFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .strict(),
])

export const textGreetingUpdateSchema = z
  .object({
    expectedRevision: z.number().int().nonnegative(),
    template: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .refine(
        (value) =>
          value.includes('{assistantName}') &&
          value.includes('{clinicName}') &&
          !/\{(?!assistantName\}|clinicName\})[^{}]+\}/.test(value),
        'Use only {assistantName} and {clinicName}, and include both.'
      ),
  })
  .strict()

export const voiceAIControlSettingsResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    turnOffAI: z.boolean(),
    voiceEnabled: z.boolean(),
    voiceActive: z.boolean(),
    mainPhone: z.string().regex(/^\+[1-9][0-9]{7,14}$/).nullable(),
    mainPhoneRoutable: z.boolean(),
  })
  .strict()

export const voiceAIControlSettingsUpdateSchema = z
  .object({
    expectedTurnOffAI: z.boolean(),
    turnOffAI: z.boolean(),
  })
  .strict()

const specialRuleSchema = z.object({
  ruleId: z.string().regex(/^rule-[0-9a-f]{24}$/),
  schemaVersion: z.literal(1),
  status: z.enum(['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED']),
  content: z.string().min(1).max(1000),
  contentFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  revision: z.number().int().positive(),
  createdAtMs: z.number().int().nonnegative(),
  updatedAtMs: z.number().int().nonnegative(),
  approvedAtMs: z.number().int().nonnegative().nullable(),
  archivedAtMs: z.number().int().nonnegative().nullable(),
  scope: z.enum(['GENERAL', 'VOICE_ONLY', 'TEXT_ONLY']),
})

export const specialRulesResponseSchema = z.object({
  specialRules: z.object({
    schemaVersion: z.literal(1),
    revision: z.number().int().nonnegative(),
    collectionFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    rules: z.array(specialRuleSchema).max(200),
  }),
  rule: specialRuleSchema.optional(),
})

export const voiceServiceCatalogReconciliationResponseSchema = z
  .object({
    state: z.enum(['IN_SYNC', 'NOT_APPLICABLE']),
    catalogFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()

export const voiceServiceCatalogStatusSchema = z
  .object({
    state: z.enum(['IN_SYNC', 'PENDING', 'BLOCKED', 'NOT_APPLICABLE']),
    catalogFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()
export type VoiceServiceCatalogStatus = z.infer<
  typeof voiceServiceCatalogStatusSchema
>

export const voiceRulesReconciliationResponseSchema = z
  .object({
    state: z.literal('IN_SYNC'),
    collectionRevision: z.number().int().nonnegative(),
    collectionFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()

const serviceCatalogItemSchema = z
  .object({
    serviceId: z.string().min(1).max(128),
    name: z.string().trim().min(1).max(160),
    availability: z.boolean(),
  })
  .strict()

export const serviceCatalogResponseSchema = z
  .object({
    sourceType: z.literal('FLIO_MANAGED'),
    configured: z.boolean(),
    catalogFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    services: z.array(serviceCatalogItemSchema).max(200),
  })
  .strict()

export const serviceCatalogUpdateSchema = z
  .object({
    expectedFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    services: z
      .array(
        z
          .object({
            serviceId: z.string().min(1).max(128).optional(),
            name: z.string().trim().min(1).max(160),
            availability: z.boolean(),
          })
          .strict()
      )
      .max(200),
  })
  .strict()

export type AssistantBehavior = z.infer<typeof behaviorSchema>
export type AssistantBehaviorResponse = z.infer<
  typeof assistantBehaviorResponseSchema
>
export type AssistantBehaviorUpdate = z.infer<
  typeof assistantBehaviorUpdateSchema
>
export type VoiceGreetingUpdate = z.infer<typeof voiceGreetingUpdateSchema>
export type TextGreetingResponse = z.infer<typeof textGreetingResponseSchema>
export type TextGreetingUpdate = z.infer<typeof textGreetingUpdateSchema>
export type VoiceAIControlSettingsResponse = z.infer<
  typeof voiceAIControlSettingsResponseSchema
>
export type VoiceAIControlSettingsUpdate = z.infer<
  typeof voiceAIControlSettingsUpdateSchema
>
export type SpecialRulesResponse = z.infer<typeof specialRulesResponseSchema>
export type SpecialRule = z.infer<typeof specialRuleSchema>
export type ServiceCatalogResponse = z.infer<
  typeof serviceCatalogResponseSchema
>
export type ServiceCatalogUpdate = z.infer<typeof serviceCatalogUpdateSchema>
